import React from "react";
import clsx from "clsx";
import get from "lodash/get";

import { Dialog, DialogContent, DialogTitle } from "@material-ui/core";
import IconButton from "@material-ui/core/IconButton";
import { Close } from "@material-ui/icons";

import { racehorse360 } from "@tsg/1st-grpc-web-rh360";

import { useRacehorse360Api } from "hooks/api";
import Loader from "components/Loader";
import { SortOrder, SortOrderExtended } from "interfaces/SortOrder";
import { columns } from "./helpers";
import useStyles from "./styles";

interface IProps {
  onClose: () => void;
  horseName: string;
  horseId: string;
}

const RisksHistoryPopup = (props: IProps) => {
  const { onClose, horseId, horseName } = props;

  const classes = useStyles();
  const { useListHorseRisks } = useRacehorse360Api();

  const { data: horseRisksData, isLoading: horseRisksLoading } =
    useListHorseRisks({
      query: {
        horseIds: [horseId]
      },
      getOptions: {
        select: [
          "id",
          "identified_on",
          "expired_on",
          "risk_factor.short_description",
          "risk_factor.detailed_description"
        ],
        orderBy: [
          `identified_on ${SortOrder.DESC}`,
          `expired_on ${SortOrderExtended.DESC_NULLS_FIRST}`
        ]
      }
    });

  const renderTableHeader = () => {
    return (
      <div className={classes.tableHeader}>
        {columns.map((column, index) => (
          <span
            key={column.id}
            className={clsx(classes.cell, classes.headCell, {
              [`${classes.centerCell}`]: index
            })}
          >
            {column.title}
          </span>
        ))}
      </div>
    );
  };

  const renderTableContent = () => {
    const horseRisks: racehorse360.IHorseRisk[] = horseRisksData.horseRisks;
    return (
      <div className={classes.tableContent}>
        {horseRisks.map(risk => (
          <div key={risk.id} className={classes.tableRow}>
            <div
              className={clsx(classes.tableRowContent, {
                [`${classes.alert}`]: !risk.expiredOn
              })}
            >
              {columns.map((column, index) => (
                <div
                  key={column.id}
                  className={clsx(classes.cell, {
                    [`${classes.centerCell}`]: index
                  })}
                >
                  {column.renderer ? (
                    column.renderer(get(risk, column.field))
                  ) : (
                    <>
                      <div className={classes.shortDescription}>
                        {risk.riskFactor.shortDescription}
                      </div>
                      {risk.riskFactor.detailedDescription && (
                        <div className={classes.detailed}>
                          {risk.riskFactor.detailedDescription}
                        </div>
                      )}
                    </>
                  )}
                </div>
              ))}
            </div>
          </div>
        ))}
      </div>
    );
  };

  const renderTable = () => (
    <>
      {renderTableHeader()}
      {renderTableContent()}
    </>
  );

  return (
    <Dialog
      open={true}
      classes={{
        paper: classes.paper
      }}
      onClose={onClose}
    >
      <DialogTitle className={classes.muiDialogTitle}>
        <div className={classes.dialogHeader}>
          <span>Risk Factors</span>
          <IconButton onClick={onClose} className={classes.closeIcon}>
            <Close />
          </IconButton>
        </div>
        <div className={classes.horseName}>{horseName}</div>
      </DialogTitle>

      <DialogContent className={classes.muiDialogContent}>
        {horseRisksLoading ? <Loader /> : renderTable()}
      </DialogContent>
    </Dialog>
  );
};

export default RisksHistoryPopup;
